/**
 * DonorGivingTab — the Giving + Engagement view on a donor record.
 *
 * Mounted on BOTH the contact and the member profile. It used to live only on
 * contacts, so a program member who donates had no giving view at all.
 *
 * Two Phase-4/5 corrections beyond that:
 *  - the cultivation stage now comes from a server-side lookup by donor. It was
 *    computed by pulling the whole pipeline and filtering on `donorMemberId`, a
 *    field nothing ever set — so the panel was empty by construction.
 *  - receipts are actionable: open the PDF, re-send, or pull a year-end statement.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Award,
  Calendar,
  FileText,
  Gift,
  Loader2,
  Mail,
  Send,
  Star,
  Target,
  UserPlus,
} from 'lucide-react';
import {
  Donation,
  issueReceipt,
  listDonations,
  openAnnualStatement,
  openReceiptPdf,
} from '@/services/api/donations';
import { DonorOpportunity, listDonorOpportunities } from '@/services/api/donorPipeline';
import { MemberDonorEvent, listMemberDonorEvents } from '@/services/api/donorEvents';
import {
  DONOR_STATUSES,
  DonorProfile,
  DonorStatus,
  getDonor,
  markAsDonor,
  updateDonor,
} from '@/services/api/donorProfiles';

function money(n: number): string {
  return `$${Math.round(n).toLocaleString('en-US')}`;
}

function fmtDate(s?: string | null): string {
  return s ? new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
}

export default function DonorGivingTab({ contactId }: { contactId: number }) {
  const [loading, setLoading] = useState(true);
  const [gifts, setGifts] = useState<Donation[]>([]);
  const [opps, setOpps] = useState<DonorOpportunity[]>([]);
  const [events, setEvents] = useState<MemberDonorEvent[]>([]);
  const [profile, setProfile] = useState<DonorProfile | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [g, o, e, p] = await Promise.all([
      listDonations({ donor_member_id: contactId, limit: 200 }).catch(() => [] as Donation[]),
      // Server-side filter — no more fetching the entire board per contact.
      listDonorOpportunities({ donor_member_id: contactId }).catch(() => [] as DonorOpportunity[]),
      listMemberDonorEvents(contactId).catch(() => [] as MemberDonorEvent[]),
      getDonor(contactId).catch(() => null),
    ]);
    setGifts(g);
    setOpps(o);
    setEvents(e);
    setProfile(p);
    setLoading(false);
  }, [contactId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  const succeeded = gifts.filter((g) => g.status === 'succeeded');
  const lifetime = succeeded.reduce((s, g) => s + Number(g.amount || 0), 0);
  const now = new Date();
  const thisYear = now.getFullYear();
  const ytd = succeeded
    .filter((g) => new Date(g.created_at).getFullYear() === thisYear)
    .reduce((s, g) => s + Number(g.amount || 0), 0);
  const attendedCount = events.filter((e) => e.attended).length;
  const activeOpp = opps.find((o) => o.stage !== 'stewardship' && o.stage !== 'declined') || opps[0];

  const act = async (key: string, fn: () => Promise<unknown>, ok: string) => {
    setBusy(key);
    setMsg(null);
    try {
      await fn();
      setMsg(ok);
      await load();
    } catch (e: any) {
      setMsg(e?.message || 'That did not work.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-5">
      {/* Donor status — the record-level answer to "is this person a donor?" */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <Star size={16} className={profile?.is_donor ? 'text-amber-500' : 'text-gray-300'} />
          {profile?.is_donor ? (
            <>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Donor</span>
              <select
                value={profile.donor_status}
                onChange={(e) =>
                  act('status', () => updateDonor(contactId, { donor_status: e.target.value as DonorStatus }), 'Updated.')
                }
                className="rounded border border-gray-300 px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              >
                {DONOR_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              {!profile.explicit ? (
                <span className="text-xs text-gray-400">(from giving history)</span>
              ) : null}
            </>
          ) : (
            <>
              <span className="text-sm text-gray-500">Not marked as a donor</span>
              <button
                onClick={() => act('mark', () => markAsDonor({ member_id: contactId }), 'Marked as a donor.')}
                disabled={busy === 'mark'}
                className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <UserPlus size={12} /> Mark as donor
              </button>
            </>
          )}
        </div>
        {lifetime > 0 ? (
          <button
            onClick={() => act('stmt', () => openAnnualStatement(contactId, thisYear), 'Statement opened.')}
            disabled={busy === 'stmt'}
            className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            {busy === 'stmt' ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}
            {thisYear} giving statement
          </button>
        ) : null}
      </div>

      {msg ? <p className="text-xs text-gray-600 dark:text-gray-300">{msg}</p> : null}

      {/* Summary */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: 'Lifetime giving', value: money(lifetime) },
          { label: 'Given YTD', value: money(ytd) },
          { label: 'Gifts', value: String(succeeded.length) },
          { label: 'Events attended', value: String(attendedCount) },
        ].map((s) => (
          <div key={s.label} className="rounded-lg border border-gray-100 p-3 dark:border-gray-700">
            <div className="text-xs text-gray-500">{s.label}</div>
            <div className="mt-0.5 text-xl font-semibold text-gray-900 dark:text-gray-100">{s.value}</div>
          </div>
        ))}
      </div>

      {/* Cultivation */}
      <section>
        <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300">
          <Target size={15} /> Cultivation
        </h4>
        {activeOpp ? (
          <div className="rounded-lg border border-gray-100 p-3 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium capitalize text-gray-900 dark:text-gray-100">{activeOpp.stage}</span>
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{money(activeOpp.amount)}</span>
            </div>
            {activeOpp.closeDate ? (
              <p className="mt-0.5 text-xs text-gray-400">Expected {fmtDate(activeOpp.closeDate)}</p>
            ) : null}
            {opps.length > 1 ? (
              <p className="mt-1 text-xs text-gray-400">{opps.length} asks in the pipeline</p>
            ) : null}
          </div>
        ) : (
          <p className="rounded-lg border border-dashed border-gray-200 px-3 py-4 text-center text-sm text-gray-400 dark:border-gray-700">
            No ask in the pipeline. Add one from the Donor pipeline board.
          </p>
        )}
      </section>

      {/* Gifts + receipts */}
      <section>
        <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300">
          <Gift size={15} /> Gift history
        </h4>
        {gifts.length === 0 ? (
          <p className="rounded-lg border border-dashed border-gray-200 px-3 py-4 text-center text-sm text-gray-400 dark:border-gray-700">
            No gifts recorded yet.
          </p>
        ) : (
          <div className="divide-y rounded-lg border border-gray-100 dark:divide-gray-700 dark:border-gray-700">
            {gifts.map((g) => (
              <div key={g.id} className="flex items-center justify-between gap-2 px-3 py-2">
                <div className="min-w-0">
                  <div className="text-sm text-gray-900 dark:text-gray-100">
                    {money(Number(g.amount))}{' '}
                    <span className="text-xs text-gray-400">
                      {g.method} · {fmtDate(g.created_at)}
                    </span>
                  </div>
                  <div className="truncate text-xs text-gray-400">
                    {g.event_title ? `${g.event_title} · ` : ''}
                    {g.campaign || ''}
                    {g.receipt_state === 'sent'
                      ? ` · receipt ${g.receipt_number} sent`
                      : g.receipt_number
                        ? ` · receipt ${g.receipt_number} not sent`
                        : ' · no receipt'}
                  </div>
                </div>
                {g.status === 'succeeded' ? (
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      onClick={() => act(`pdf${g.id}`, () => openReceiptPdf(g.id), 'Receipt opened.')}
                      disabled={busy !== null}
                      title="Open the receipt"
                      className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700"
                    >
                      {busy === `pdf${g.id}` ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                    </button>
                    <button
                      onClick={() => act(`send${g.id}`, () => issueReceipt(g.id), 'Receipt sent.')}
                      disabled={busy !== null}
                      title={g.receipt_sent_at ? 'Re-send the receipt' : 'Send the receipt'}
                      className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700"
                    >
                      {busy === `send${g.id}` ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Engagement */}
      <section>
        <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300">
          <Calendar size={15} /> Events
        </h4>
        {events.length === 0 ? (
          <p className="rounded-lg border border-dashed border-gray-200 px-3 py-4 text-center text-sm text-gray-400 dark:border-gray-700">
            Not invited to any donor events yet.
          </p>
        ) : (
          <div className="divide-y rounded-lg border border-gray-100 dark:divide-gray-700 dark:border-gray-700">
            {events.map((e) => (
              <div key={e.id} className="flex items-center justify-between px-3 py-2">
                <div className="min-w-0">
                  <div className="truncate text-sm text-gray-900 dark:text-gray-100">{e.title}</div>
                  <div className="text-xs text-gray-400">
                    {fmtDate(e.start_time)} · {e.response_status}
                    {e.invite_sent_at ? '' : ' · never notified'}
                  </div>
                </div>
                {e.attended ? (
                  <span className="flex shrink-0 items-center gap-1 rounded bg-green-50 px-1.5 py-0.5 text-[11px] text-green-700 dark:bg-green-900/30 dark:text-green-300">
                    <Award size={11} /> attended
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </section>

      {profile?.solicitation_opt_out ? (
        <div className="flex items-center gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-300">
          <Mail size={13} /> This donor has opted out of solicitations.
        </div>
      ) : null}
    </div>
  );
}
